import { fetcher } from '@data/utils/api/fetcher/fetcher';
import formatDate from '@data/utils/format-date';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { classOverviewSchema } from './class-overview';
import { contentStatusTableSchema } from './content-status-table';
import { teacherOverviewSchema } from './teacher-overview';
import { classActivityFeedSchema } from './class-activity';
import { engagementFeedSchema } from './engagement-feed';
import { annotationsCountTableSchema } from './annotations-count';
import { quizScoreTableSchema } from './quiz-score';
import { orgActiveStudentsSchema } from './org-active-students';

export type { ContentStatusTableResponse } from './content-status-table';
export type { TeacherOverviewResponse } from './teacher-overview';
export type { ClassOverviewResponse } from './class-overview';
export type { EngagementFeedResponse } from './engagement-feed';
export type { AnnotationsCountTableResponse } from './annotations-count';
export type { QuizScoreTableResponse } from './quiz-score';

const tableSchema = z.discriminatedUnion('type', [
  contentStatusTableSchema,
  teacherOverviewSchema,
  classOverviewSchema,
  classActivityFeedSchema,
  engagementFeedSchema,
  annotationsCountTableSchema,
  quizScoreTableSchema,
  orgActiveStudentsSchema,
]);

export type MetricsTableResponse = z.infer<typeof tableSchema>;

export interface UseMetricsTableApiProps<T extends MetricsTableResponse> {
  metricType: T['type'];
  orgId?: string;
  teacherId?: string;
  classId?: string;
  studentId?: string;
  familyId?: string;
  contentId?: string;
  activityId?: string;
  products: string[];
  startDate?: Date;
  endDate?: Date;
  groupBy?: string;
}

const getMetricsTableApi = <T extends MetricsTableResponse>({
  metricType,
  orgId,
  teacherId,
  classId,
  studentId,
  familyId,
  contentId,
  activityId,
  products,
  startDate,
  endDate,
  groupBy,
}: UseMetricsTableApiProps<T>) =>
  fetcher('/metrics-table', {
    queryParameters: {
      metricType,
      orgId,
      teacherId,
      classId,
      studentId,
      familyId,
      contentId,
      activityId,
      products: products?.join(','),
      startDate: formatDate(startDate),
      endDate: formatDate(endDate, { boundary: 'end' }),
      groupBy,
    },
  }).then(
    (res) =>
      tableSchema
        .refine(
          (val) => val.type === metricType,
          (val) => ({
            message: `Requested: ${metricType}, received: ${val.type}.`,
          })
        )
        .parse(res) as T
  );

const endpointEnabled = <T extends MetricsTableResponse>({
  metricType,
  orgId,
  teacherId,
  classId,
}: UseMetricsTableApiProps<T>) => {
  if (metricType === 'classOverview' || metricType === 'engagementFeed') {
    return !!classId;
  }

  if (metricType === 'orgActiveStudents') {
    return true;
  }

  return !!orgId && !!teacherId;
};

export function useMetricsTableApi<T extends MetricsTableResponse>({
  metricType,
  orgId,
  teacherId,
  classId,
  studentId,
  familyId,
  contentId,
  activityId,
  products,
  startDate,
  endDate,
  groupBy,
}: UseMetricsTableApiProps<T>): UseQueryResult<T, Error> {
  return useQuery({
    queryKey: [
      'metrics-table',
      metricType,
      orgId,
      teacherId,
      classId,
      studentId,
      familyId,
      contentId,
      activityId,
      products,
      formatDate(startDate),
      formatDate(endDate, { boundary: 'end' }),
      groupBy,
    ].filter(Boolean),
    queryFn: () =>
      getMetricsTableApi<T>({
        metricType,
        orgId,
        teacherId,
        classId,
        studentId,
        familyId,
        contentId,
        activityId,
        products,
        startDate,
        endDate,
        groupBy,
      }),
    enabled: endpointEnabled({
      metricType,
      orgId,
      teacherId,
      classId,
      studentId,
      familyId,
      contentId,
      activityId,
      products,
      startDate,
      endDate,
      groupBy,
    }),
  });
}
